import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FormField, OrgTreeSelect, SelectField } from "@/components/common";
import { useTran } from "@/hooks";
import { formatYear } from "@/features/esg/utils/year-helpers";
import { observer } from "mobx-react-lite";
import {
  useDisclosureStore,
  useEntryDataStore,
  useEntryUIStore,
} from "../../hooks/useDataEntryStore";
import {
  collectSelectableOrgIds,
  makeTreewithAllowOrgIds,
} from "../../utils/orgTree.helpers";
import { Search } from "lucide-react";
import { reportingYearStore } from "@/features/reporting-boundary/stores/reporting-year.store";
import { formatReportingYearPeriod } from "@/features/esg/utils/period-constants";
import { normalizeOrgId } from "@/features/esg/utils/org-scope.utils";
import {
  countPrunedCarriedIds,
  parseCarriedIds,
} from "@/features/esg/utils/scope-carry.utils";
import { useAuthStore } from "@/features/auth/hooks";

const EntryFilterBar = observer(() => {
  const { t } = useTran();
  const { user } = useAuthStore();
  const [searchParams] = useSearchParams();
  const { activeDisclosure } = useDisclosureStore();
  const { disclosureOrgTree, orgTreeLoaded } = useEntryDataStore();
  const {
    selectedOrg,
    selectedYear,
    searchEntry,
    setSelectedOrg,
    setSelectedYear,
    setSearchEntry,
  } = useEntryUIStore();

  const sortedYears = useMemo(() => {
    const masterDataYears = activeDisclosure?.masterDataYears || [];
    return masterDataYears.slice().sort((a, b) => b - a);
  }, [activeDisclosure?.masterDataYears]);

  const { reportingYears } = reportingYearStore;

  const hasMultipleYears = sortedYears.length > 1;
  // const yearOptions = useMemo(() => {
  //   const years = sortedYears.map((year) => ({
  //     value: String(year),
  //     label: formatYear(year),
  //   }));
  //   // Skip the "All Years" pseudo-option when only one real year exists —
  //   // same reasoning as the single-org "All Organizations" suppression.
  //   return hasMultipleYears
  //     ? [{ value: "all", label: t("All Years") }, ...years]
  //     : years;
  // }, [sortedYears, hasMultipleYears, t]);

  const yearOptions = useMemo(() => {
    const years = sortedYears
      .map((year) => {
        const reportingYear = reportingYears.find(
          (ry) => String(ry.startYear) === String(year),
        );

        return {
          value: String(year),
          label: reportingYear
            ? formatReportingYearPeriod(reportingYear)
            : formatYear(year), // fallback
        };
      })
      .filter(Boolean);

    return hasMultipleYears
      ? [{ value: "all", label: t("All Years") }, ...years]
      : years;
  }, [sortedYears, reportingYears, hasMultipleYears, t]);

  const selectedYearMetricOrgIds = useMemo(() => {
    if (!selectedYear || selectedYear === "all") {
      return null;
    }

    const masterDataRecords = activeDisclosure?.masterDataRecords || [];
    const yearRecords = masterDataRecords.filter(
      (record) => Number(record.year) === Number(selectedYear),
    );

    if (yearRecords.some((record) => record.isAllOrganization)) {
      return null;
    }

    return new Set(
      yearRecords
        .flatMap((record) => [
          ...(record.isCompanySelected && user?.companyId
            ? [user.companyId]
            : []),
          ...(record.organizationIds || []),
        ])
        .map(normalizeOrgId)
        .filter(Boolean),
    );
  }, [activeDisclosure?.masterDataRecords, selectedYear, user?.companyId]);

  const availableOrgTree = useMemo(() => {
    if (!selectedYearMetricOrgIds) return disclosureOrgTree;
    return makeTreewithAllowOrgIds(disclosureOrgTree, selectedYearMetricOrgIds);
  }, [disclosureOrgTree, selectedYearMetricOrgIds]);

  const selectableOrgIds = useMemo(
    () => collectSelectableOrgIds(availableOrgTree),
    [availableOrgTree],
  );
  const hasMultipleOrgChoices = selectableOrgIds.length > 1;

  const disclosureId = activeDisclosure?._id;
  const urlMetricId = searchParams.get("metricId");
  const isUrlTargetedDisclosure = urlMetricId && urlMetricId === disclosureId;

  // Org ids carried in from a dashboard drill-through, kept to detect (and
  // say) when the arrival scope pruned part of the dashboard selection.
  // Cleared once the user edits the org or year selection themselves. The
  // notice flag is sticky: set when pruning actually removes a carried id,
  // so a later scope change cannot silently retract it.
  const [carriedOrgIds, setCarriedOrgIds] = useState(null);
  const [prunedCarriedNotice, setPrunedCarriedNotice] = useState(false);

  const clearCarriedScope = () => {
    setCarriedOrgIds(null);
    setPrunedCarriedNotice(false);
  };

  // When the disclosure scope resolves to exactly one org/year, auto-select
  // it so the trigger label reflects the actual scope instead of "All ...".
  // Keyed on disclosureId so manual deselect after this fires isn't reverted
  // until the user navigates to another disclosure.
  // If URL params target this disclosure, use URL values instead.
  useEffect(() => {
    if (!disclosureId) return;

    if (isUrlTargetedDisclosure) {
      // Multi-value scope-carry contract: comma-joined ids, "all" = [].
      const urlOrgIds = parseCarriedIds(searchParams.get("organization"));
      setSelectedOrg(urlOrgIds);
      setCarriedOrgIds(urlOrgIds.length > 0 ? urlOrgIds : null);
      setPrunedCarriedNotice(false);

      // The year selector is single-value; a multi-year carry can only be
      // represented as All Years here.
      const urlYears = parseCarriedIds(searchParams.get("year"));
      setSelectedYear(urlYears.length === 1 ? urlYears[0] : "all");
      return;
    }

    // Navigating to a disclosure the URL does not target ends the carried
    // arrival — the notice must not follow the user to other metrics.
    clearCarriedScope();

    if (selectableOrgIds.length === 1) {
      const onlyId = selectableOrgIds[0];
      if (!(selectedOrg.length === 1 && selectedOrg[0] === onlyId)) {
        setSelectedOrg([onlyId]);
      }
    }
    const hasSelectedYear = sortedYears.some(
      (year) => String(year) === String(selectedYear),
    );

    if (sortedYears.length === 1) {
      // Only one available year -> auto select it
      const onlyYear = String(sortedYears[0]);

      if (selectedYear !== onlyYear) {
        setSelectedYear(onlyYear);
      }
    } else if (hasSelectedYear) {
      // Keep current selected year if it still exists
    } else if (selectedYear !== "all") {
      // Current year is not available -> reset to All Years
      setSelectedYear("all");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disclosureId]);

  useEffect(() => {
    if (!selectedOrg.length) return;
    // Prune only after the org hierarchy has actually loaded; before that
    // the selectable set is empty because nothing has arrived, and pruning
    // would wipe a carried selection prematurely. Emptiness itself is not
    // the signal: once loaded, an empty selectable set is a genuine
    // zero-overlap scope and must still clear the carried selection (and
    // raise the notice) instead of keeping it silently.
    if (!orgTreeLoaded) return;

    const selectableSet = new Set(selectableOrgIds.map(normalizeOrgId));
    const nextSelectedOrg = selectedOrg.filter((orgId) =>
      selectableSet.has(normalizeOrgId(orgId)),
    );

    if (nextSelectedOrg.length !== selectedOrg.length) {
      // Gated on the URL still targeting this disclosure: when navigating
      // to another disclosure, this effect can run against the same
      // render's stale carriedOrgIds before the clear above lands, and the
      // notice must not follow the user there.
      if (
        isUrlTargetedDisclosure &&
        countPrunedCarriedIds(carriedOrgIds, selectableOrgIds) > 0
      ) {
        setPrunedCarriedNotice(true);
      }
      setSelectedOrg(nextSelectedOrg);
    }
  }, [
    orgTreeLoaded,
    selectableOrgIds,
    selectedOrg,
    setSelectedOrg,
    carriedOrgIds,
    isUrlTargetedDisclosure,
  ]);

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 bg-muted/50 rounded-lg p-3">
        <SelectField
          value={selectedYear}
          options={yearOptions}
          className="w-full sm:w-[230px]"
          onValueChange={(year) => {
            clearCarriedScope();
            setSelectedYear(year);
          }}
          sortOptions={false}
          contentClassName="w-max min-w-[var(--radix-popover-trigger-width,var(--radix-select-trigger-width))] max-w-[calc(100vw-1rem)] [&_[data-slot=searchable-select-label]]:overflow-visible [&_[data-slot=searchable-select-label]]:whitespace-nowrap"
        />

        <OrgTreeSelect
          tree={availableOrgTree}
          value={selectedOrg}
          onChange={(ids) => {
            clearCarriedScope();
            setSelectedOrg(ids);
          }}
          placeholder={t("All Organizations")}
          helperText=""
          showAllOption={hasMultipleOrgChoices}
          multiSelect
          showBranchSelectAll
          branchSelectLevels={["parent_company", "level_1"]}
          className="w-full sm:w-[240px]"
        />

        <FormField
          value={searchEntry}
          onChange={(e) => setSearchEntry(e.target.value)}
          placeholder={t("Entry name...")}
          leftIcon={<Search size={16} />}
          className="flex-1"
        />
      </div>
      {prunedCarriedNotice && (
        <p className="px-1 text-xs text-muted-foreground">
          {t(
            "Some organizations from your dashboard selection do not apply to this metric and were removed.",
          )}
        </p>
      )}
    </div>
  );
});

export default EntryFilterBar;
